
import { useEffect, useState, useContext, useRef } from "react";
import { createOrder, verifyPayment, getPremiumQuestions } from "../api/api";
import api from "../api/api";
import Loader from "../components/Loader";
import { AuthContext } from "../context/AuthContext";

export default function UserPanel() {
  const { user } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [language, setLanguage] = useState("javascript");
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [solution, setSolution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [customTitle, setCustomTitle] = useState("");

  const skipFetch = useRef(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        if (!user) return; // agar login nahi hai

        let res;
        if (user.isPremium) {
          res = await getPremiumQuestions();
        } else {
          res = await api.get("/auth/questions");
        }

        setQuestions(res.data.questions || res.data.data || []);
        setSelectedQuestion("");
      } catch (err) {
        console.error(err);
        setError("Failed to fetch questions");
      }
    };
    fetchQuestions();
  }, [user]); // ✅ depend only on user





  useEffect(() => {
    if (user?.isPremium) {
      setHasPaid(true);
      setIsPremium(true);
    }
  }, [user]);
  // ✅ Suggestions
  useEffect(() => {
    if (skipFetch.current) {
      skipFetch.current = false;
      return;
    }
    if (!search.trim()) {
      setSuggestions([]);
      return;
    }


    setSearching(true);
    const delayDebounce = setTimeout(async () => {
      try {
        const res = await api.post("/ai/search-suggestions", { query: search });
        setSuggestions(res.data.suggestions || []);
      } catch (err) {
        console.error("Suggestion fetch error:", err);
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 700);

    return () => clearTimeout(delayDebounce);
  }, [search]);

  // ✅ Razorpay Payment Function
  const handlePayment = async () => {
    try {
      const { data } = await createOrder({
        amount: 100, // paisa me (100 = ₹1 agar tumne Razorpay me INR paise enable kiya hai)
        currency: "INR",
      });

      const order = data.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "DSABuddy",
        description: "DSA Buddy Premium Subscription",
        order_id: order.id,
        handler: async function (response) {
          const verifyRes = await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (verifyRes.data.success) {
            alert("Payment Successful ✅ Premium Unlocked!");
            setHasPaid(true);
          } else {
            alert("Payment Verification Failed ❌");
          }
        },
        prefill: {
          name: user?.name || "Your User",
          email: user?.email || "user@example.com",
          contact: user?.phone,
        },
        theme: {
          color: "#3399cc",
        },
      };

      if (!window.Razorpay) {
        alert("Razorpay SDK not loaded");
        return;
      }

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      alert("Payment initialization failed ❌");
    }
  };


  // ✅ Solve Question
  const handleSolve = async (e) => {
    e.preventDefault();
    if (!selectedQuestion) {
      setError("Please select a question");
      return;
    }
    setLoading(true);
    setError("");
    setSolution(null);

    try {
      const res = await api.post("/ai/solve", {
        questionId: selectedQuestion,
        search,
        userInput,
        language,
      });
      setSolution(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Error solving question");
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto mt-10 p-6 bg-white shadow-md rounded-md">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">User Panel</h1>
      <p className="mb-4 text-gray-600">Welcome, {user?.name || "Guest"}!</p>

      {error && <p className="text-red-600 mb-3">{error}</p>}

      {/* Question Type Dropdown */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Select Question Type</label>
        <select
          value={isPremium ? "premium" : "normal"}
          onChange={(e) => setIsPremium(e.target.value === "premium")}

          className="w-full border border-gray-300 rounded-md px-3 py-2"
        >
          <option value="normal">Normal Questions</option>
          <option value="premium">Premium Questions 🔑</option>
        </select>

      </div>

      {/* Payment Section */}
      {isPremium && !hasPaid && (
        <div className="mb-6 text-center">
          <h2 className="text-lg font-semibold mb-3">Buy Premium Access</h2>
          <button
            onClick={handlePayment}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
          >
            Pay ₹100 to Unlock Premium
          </button>
        </div>
      )}

      {/* Question Form */}
      {(!isPremium || hasPaid) && (
        <form onSubmit={handleSolve} className="space-y-6">
          {/* Select Question */}
          <div>
            <label className="block font-medium mb-1">Select Question</label>
            <select
              value={selectedQuestion}
              onChange={(e) => {
                const selected = questions.find(q => q._id === e.target.value);
                setSelectedQuestion(e.target.value);

                if (selected?.link) {
                  // Normal Question link
                  setCustomTitle("Normal Question Selected");
                  window.open(selected.link, "_blank");
                } else if (selected?.premiumLink && isPremium && hasPaid) {
                  // Premium Question link
                  if (selected?.premiumLink && isPremium && hasPaid) {
                    setCustomTitle("Premium Question Selected");

                    const url = selected.premiumLink.startsWith("http")
                      ? selected.premiumLink
                      : `https://dsa-buddy-back.vercel.app${selected.premiumLink}`;

                    window.open(url, "_blank");
                  }

                }
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            >
              <option value="">-- Choose a Question --</option>
              {questions.map((q) => (
                <option key={q._id} value={q._id}>
                  {selectedQuestion === q._id
                    ? customTitle || q.title
                    : q.title}
                </option>
              ))}
            </select>



          </div>
          {/* Show only if NOT Premium */}
          {!isPremium && (
            <>
              <div>
                <label className="block font-medium mb-1">Select Language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                </select>
              </div>

              {/* Search with Suggestions */}
              <div className="relative">
                <label className="block font-medium mb-1">Search Topic</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g. Graphs, Trees, DP..."
                />

                {searching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600">
                    <Loader />
                  </div>
                )}

                {suggestions.length > 0 && (
                  <ul className="absolute z-10 bg-white border border-gray-300 rounded-md w-full mt-1 max-h-40 overflow-y-auto shadow">
                    {suggestions.map((s, idx) => (
                      <li
                        key={idx}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          skipFetch.current = true;
                          setSearch(s);
                          setSuggestions([]);
                          document.activeElement.blur();
                        }}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* User Input */}
              <div>
                <label className="block font-medium mb-1">Your Input</label>
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g. [1,2,4,5,7]"
                />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 transition disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader /> Solving...
              </>
            ) : (
              "Solve Question"
            )}
          </button>
        </form>
      )}

      {/* Solution Section */}
      {solution && (
        <div className="mt-6 bg-gray-50 p-4 rounded-md shadow">
          <h2 className="text-xl font-semibold mb-2">DSABuddy Solution</h2>
          {solution.code && (
            <pre className="bg-black text-green-400 p-3 rounded-md overflow-x-auto">
              {solution.code}
            </pre>
          )}
          <h3 className="text-lg font-medium mt-3">Explanation</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{solution.explanation}</p>
          {solution.output && (
            <>
              <h3 className="text-lg font-medium mt-3">Expected Output</h3>
              <p className="bg-gray-200 p-2 rounded">{solution.output}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
